import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';

import { Grid, withTheme, Button, FormControlLabel, Radio, TextField } from '@material-ui/core';
import config from '../../config';

import BookingProgressBar from '../../components/fancy/BookingProgressBar';
import BookingSummaryContainer from '../../containers/BookingSummaryContainer';
import {
    PAYMENT_METHOD_TYPES_AGENT_ONLY,
    PAYMENT_METHODS,
    usePaymentMethods
} from '../../hooks/usePaymentMethod';
import { resetUrlFilters } from '../activitycards/components/urlFiltering';
import {
    InvisibleAccordionSummary,
    InvisibleAccordionDetails,
    InvisibleAccordion
} from './components/InvisibleAccordion';
import TermsAndConditions from './components/TermsAndConditions';
import { NoGuestPayment } from './components/NoGuestPayment';
import PaymentFormCard from './components/PaymentFormCard';
import PaymentFormHold from './components/PaymentFormHold';
import PaymentFormOnhouse from './components/PaymentFormOnhouse';
import PaymentFormTerminal from './components/PaymentFormTerminal';
import PaymentNotifications from './components/PaymentNotifications';
import PaymentFormCash from './components/PaymentFormCash';
import PaymentFormCheck from './components/PaymentFormCheck';
import PaymentFormBalance from './components/PaymentFormBalance';
import AutocompleteSelect from '../../components/atom/AutocompleteSelect';
import { sourceAutocomplete } from '../../autocomplete';

import PaymentUserRegistrationForm from './components/PaymentUserRegistrationForm';
import CancellationPolicy from './components/CancellationPolicy';

const stripePromise = loadStripe(config.stripeKey);

const PaymentPage = ({
    cartItems,
    confirmOrder,
    customer,
    user,
    termsAndConditions,
    setTermsAndConditions,
    theme,
    setLoggedInUser
}) => {
    const [source_id, setSourceId] = useState(customer?.source_id || null);
    const [note, setNote] = useState('');
    const [paymentIntent, setPaymentIntent] = useState('');
    const clientPaymentMethods = usePaymentMethods();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        clientPaymentMethods?.length > 0 ? clientPaymentMethods[0].slug : null
    );
    const [bookingNotification, setBookingNotification] = useState(
        customer?.notification ? [...customer.notification] : ['email']
    );
    const history = useHistory();
    const [hasEnoughBalance, setHasEnoughBalance] = useState(false);
    const [estimatedTotal, setEstimatedTotal] = useState(false);

    // Available payment methods for the current cart (based on user role and payment methods available)
    const availablePaymentMethodsForCart = useMemo(() => {
        return clientPaymentMethods.filter(method => {
            const isAgentOnly = PAYMENT_METHOD_TYPES_AGENT_ONLY.includes(method.methodtype);
            // if something is not agent only, it can stay
            if (!isAgentOnly) {
                return true;
            }
            // if the user is agent and the method is agent only, it can stay
            if (['agent', 'admin'].includes(user?.role) && isAgentOnly) {
                return true;
            }

            return false;
        });
    }, [user, clientPaymentMethods]);

    // set default payment method based on user role and payment methods available
    useEffect(() => {
        // should default to the first available payment method
        const availableSlugs = availablePaymentMethodsForCart.map(m => m.slug);
        if (!availableSlugs.includes(selectedPaymentMethod) && availableSlugs.length > 0) {
            setSelectedPaymentMethod(availableSlugs[0]);
        }
    }, [availablePaymentMethodsForCart, selectedPaymentMethod, setSelectedPaymentMethod]);

    // This one is for alice booking, which is not used currently
    // const [data, loading, error] = useGetApiCall(`/rest/alice/guests?reservationNumber=${note}`);

    if (!cartItems || !cartItems.length) {
        return <Redirect to="/" />;
    }

    /**
     * Check if the payment form is valid (needs to be refactored into components and hooks)
     * @returns {boolean}
     */
    function validPaymentForm() {
        const method = availablePaymentMethodsForCart.find(m => m.slug === selectedPaymentMethod);

        if (!method) {
            alert('Please select a payment method!');
            return false;
        }

        const selectedPaymentMethodType = method?.methodtype;
        // on card payments, we need stripe customer + last4
        if ([PAYMENT_METHODS.card, PAYMENT_METHODS.hold].includes(selectedPaymentMethodType)) {
            if (!customer || !customer.stripe_last4) {
                return false;
            }
        }
        // terminal payment requires paymentIntent
        if (selectedPaymentMethodType === PAYMENT_METHODS.terminal) {
            if (!paymentIntent) {
                return false;
            }
        }

        /* We can book into negative balance, so no need to block here
        if (selectedPaymentMethodType === PAYMENT_METHODS.balance) {
            if (!hasEnoughBalance) {
                return false;
            }
        }*/
        // some payment types require a note
        if (method.note_required === true) {
            if (!note) {
                return false;
            }
        }

        // all checks passed
        return true;
    }

    function onSubmit(ev) {
        if (!validPaymentForm()) {
            ev.preventDefault();
            return;
        }

        confirmOrder(note, paymentIntent, selectedPaymentMethod, bookingNotification, source_id);
        resetUrlFilters(true);
        history.push(`/confirmation`);
    }

    // if the "customer" is not set, this is the point where we request an email address
    if (!customer && user === null) {
        return <PaymentUserRegistrationForm theme={theme} setLoggedInUser={setLoggedInUser} />;
    }

    return (
        <form onSubmit={onSubmit}>
            <Grid container spacing={2} className="page">
                <Grid item container md={8} xs={12} direction="column" justifyContent="flex-start">
                    <Link
                        to="/cart"
                        className="link mb-2"
                        style={{ color: theme.palette.primary.main }}
                    >
                        Back to Cart
                    </Link>
                    <h1 className="pageheader">Payment Information</h1>
                </Grid>
                <Grid item className="mb-2" md={4} xs={12}>
                    <BookingProgressBar activeStep={2} />
                </Grid>
                <Grid item xs={12} md={8}>
                    <div>
                        {/* no payment methods, something's wrong, either nothing is set or the role is wrong for payment */}
                        {availablePaymentMethodsForCart.length === 0 && (
                            <NoGuestPayment noPaymentMethod={clientPaymentMethods.length === 0} />
                        )}

                        {availablePaymentMethodsForCart.map(method => {
                            return (
                                <InvisibleAccordion
                                    expanded={selectedPaymentMethod === method.slug}
                                    key={method.slug}
                                >
                                    <InvisibleAccordionSummary>
                                        <FormControlLabel
                                            value={method.slug}
                                            control={
                                                <Radio
                                                    value={method.slug}
                                                    checked={selectedPaymentMethod === method.slug}
                                                    onChange={() =>
                                                        setSelectedPaymentMethod(method.slug)
                                                    }
                                                />
                                            }
                                            label={method.name}
                                        />
                                    </InvisibleAccordionSummary>
                                    <InvisibleAccordionDetails>
                                        {method.methodtype === PAYMENT_METHODS.card && (
                                            <PaymentFormCard
                                                stripePromise={stripePromise}
                                                user={user}
                                                note={note}
                                                setNote={setNote}
                                            />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.hold && (
                                            <PaymentFormHold
                                                stripePromise={stripePromise}
                                                user={user}
                                                note={note}
                                                setNote={setNote}
                                            />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.onhouse && (
                                            <PaymentFormOnhouse note={note} setNote={setNote} />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.terminal && (
                                            <PaymentFormTerminal
                                                paymentIntent={paymentIntent}
                                                setPaymentIntent={setPaymentIntent}
                                                note={note}
                                                setNote={setNote}
                                                methodSlug={method.slug}
                                            />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.cash && (
                                            <PaymentFormCash setNote={setNote} note={note} />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.check && (
                                            <PaymentFormCheck setNote={setNote} note={note} />
                                        )}
                                        {method.methodtype === PAYMENT_METHODS.balance && (
                                            <PaymentFormBalance
                                                method={method}
                                                setNote={setNote}
                                                note={note}
                                                guestId={customer?.id}
                                                setHasEnoughBalance={setHasEnoughBalance}
                                                hasEnoughBalance={hasEnoughBalance}
                                                estimatedTotal={estimatedTotal}
                                            />
                                        )}
                                    </InvisibleAccordionDetails>
                                </InvisibleAccordion>
                            );
                        })}
                    </div>
                    <div>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <PaymentNotifications
                                    bookingNotification={bookingNotification}
                                    setBookingNotification={setBookingNotification}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TermsAndConditions
                                    value={termsAndConditions}
                                    setValue={setTermsAndConditions}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <CancellationPolicy cartItems={cartItems} />
                            </Grid>
                            {user?.role === 'agent' && (
                                <Grid item xs={12}>
                                    <AutocompleteSelect
                                        initCall={sourceAutocomplete.init}
                                        autocompleteCall={sourceAutocomplete.search}
                                        value={source_id}
                                        setValue={value => setSourceId(value)}
                                    >
                                        <TextField label="Source" />
                                    </AutocompleteSelect>
                                </Grid>
                            )}
                        </Grid>
                    </div>
                </Grid>
                <Grid item xs={12} md={4}>
                    <BookingSummaryContainer
                        page="payment"
                        cartItems={cartItems}
                        payment_method={selectedPaymentMethod}
                        showCartLevelDiscounts={true}
                        setEstimatedTotal={setEstimatedTotal}
                    >
                        <Grid item xs={12} className="text-right">
                            {!termsAndConditions && (
                                <Button
                                    fullWidth
                                    className="btn-squared"
                                    variant="contained"
                                    color="primary"
                                    disabled
                                >
                                    Please accept our terms and conditions
                                </Button>
                            )}
                            {termsAndConditions && (
                                <Button
                                    fullWidth
                                    className="btn-squared"
                                    variant="contained"
                                    color="primary"
                                    type={'submit'}
                                    disabled={!validPaymentForm()}
                                >
                                    Submit Payment
                                </Button>
                            )}
                        </Grid>
                    </BookingSummaryContainer>
                </Grid>
            </Grid>
        </form>
    );
};

export default withTheme(PaymentPage);
